import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardTitle } from '../../components/ui/card';
import YoutubeVideoSolution from '../../components/solution/VideoSolution';
import OverallScoreCard from './OverallScoreCard';
import StatCard from './StatCard';
import * as PhosphorIcons from '@phosphor-icons/react';
import PercentileCard from './PercentileCard';
import VideoCipherPlayer from '../../components/solution/VideoCipherPlayer';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '../../utils/screenWidth';
import { Button } from '../../components/ui/button';

export default function Overview({
  overviewData,
  percentile,
  analysisVideo,
  analysisVideoThumbnail,
  analysisVideoCdn,
  notes,
  packId,
  testId,
  testTitle,
  groupSections,
  testShortTitle = null,
}) {
  const extractPaper = (str = '') => {
    const match = str.match(/paper\s*\d+/i);
    return match ? `Paper ${match[0].match(/\d+/)?.[0]}` : str;
  };

  const statCards = [
    {
      title: testShortTitle ? `LEADERBOARD RANK (${extractPaper(testShortTitle)})` : 'LEADERBOARD RANK',
      icon: 'TrophyIcon',
      color: 'var(--q3-pink-normal)',
      viewButton: true,
      buttonLink: `/v3/${packId}/${testId}/leaderboard`,
      numerator: overviewData?.overallData?.userRank,
      denominator: overviewData?.overallData?.totalRanks,
      tooltipText: 'Click to view Leaderboard',
    },
    {
      title: 'QS ATTEMPTED',
      icon: 'QuestionIcon',
      color: 'var(--q3-sky-normal)',
      numerator: overviewData?.overview?.questionAttempted,
      denominator: overviewData?.overview?.totalQuestions,
      tooltipText: 'Total qs attempted in the test',
    },
    {
      title: 'ACCURACY',
      icon: 'TargetIcon',
      color: 'var(--q3-fuchsia-normal)',
      numerator: overviewData?.overview?.accuracy,
      isPercentage: true,
      tooltipText: 'Overall accuracy of the test',
    },
    {
      title: 'POSITIVE SCORE',
      icon: 'ListChecksIcon',
      color: 'var(--q3-emerald-normal)',
      numerator: overviewData?.overview?.positiveScore,
      denominator: overviewData?.overview?.totalScore,
      tooltipText: 'Score if we ignore incorrect attempts',
    },
    {
      title: 'MARKS LOST',
      icon: 'XSquareIcon',
      color: 'var(--q3-danger-normal)',
      numerator: overviewData?.overview?.marksLost,
      denominator: overviewData?.overview?.totalScore,
      tooltipText: 'Marks lost due to wrong attempts',
    },
    {
      title: 'TIME TAKEN',
      icon: 'ClockIcon',
      color: 'var(--q3-amber-normal)',
      numerator: overviewData?.overview?.timeTaken,
      denominator: 'min',
      isTime: true,
      tooltipText: 'Time spent to complete the test',
    },
  ];

  const subjectWiseData = overviewData?.subjectWiseData;

  const [activeInput, setActiveInput] = useState(null);
  const [learnings, setLearnings] = useState(['', '', '']);

  const [linkedTestModalOpen, setLinkedTestModalOpen] = useState(false);
  const linkedTest = overviewData?.linkedTests?.[0];
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const navigate = useNavigate();
  const modalRef = useRef(null);

  // Helper function to compact learnings array (move non-empty items to the front)
  const compactLearnings = (arr) => {
    const nonEmpty = arr.filter((item) => item.trim() !== '');
    const empty = arr.filter((item) => item.trim() === '');
    return [...nonEmpty, ...empty].slice(0, 3);
  };

  // Parse initial notes on mount
  useEffect(() => {
    if (notes) {
      const parsed = notes.split('\n');
      const filled = [parsed[0] || '', parsed[1] || '', parsed[2] || ''];
      setLearnings(compactLearnings(filled));
    }
  }, [notes]);

  const saveNotesToServer = async (updatedNotesArray) => {
    const updatedNotesString = updatedNotesArray.join('\n');
    try {
      await axios.patch(`/api/test/${packId}/${testId}/updateNotes`, { notes: updatedNotesString });
    } catch (err) {}
  };

  const handleLearningChange = (index, value) => {
    const updated = [...learnings];
    updated[index] = value;
    setLearnings(updated);
  };

  const handleSave = (index) => {
    setActiveInput(null);
    // Compact the learnings after saving to move non-empty items to the front
    const compactedLearnings = compactLearnings(learnings);
    setLearnings(compactedLearnings);
    saveNotesToServer(compactedLearnings);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave(index);
    }
  };

  const renderIcon = (sno) => {
    const iconName = `NumberCircle${sno}Icon`;
    const Icon = PhosphorIcons[iconName];
    return Icon ? <Icon size={24} /> : null;
  };

  return (
    <div className="flex flex-col min-h-full">
      {/* Fixed header section */}
      <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0 md:space-x-4 w-full">
        {overviewData?.overallData && (
          <OverallScoreCard
            overallData={overviewData?.overallData}
            subjectWiseData={subjectWiseData}
            isPercentileAvailable={
              overviewData?.percentileObject?.isAvailable || overviewData?.percentileObject?.isPercentileDelayed
            }
            groupSections={groupSections}
            testShortTitle={testShortTitle}
          />
        )}
        {(overviewData?.percentileObject?.isAvailable || overviewData?.percentileObject?.isPercentileDelayed) && (
          <PercentileCard
            percentileData={overviewData?.percentileObject?.percentileData}
            score={overviewData?.overallData?.markScored}
            type={overviewData?.percentileObject?.percentileType}
            percentile={overviewData?.percentileObject?.calculatedPercentile}
            isProjected={overviewData?.percentileObject?.isProjected}
            isPercentileDelayed={overviewData?.percentileObject?.isPercentileDelayed}
            percentileAvailableDate={overviewData?.percentileObject?.percentileAvailableDate}
            linkedTests={overviewData?.linkedTests}
            packId={packId}
          />
        )}
      </div>

      {/* Fixed stats section */}
      <div className="my-8 grid grid-cols-1 md:grid-cols-3 flex-wrap gap-3 flex-shrink-0">
        {statCards.map((item, index) => (
          <StatCard key={index} cardMetaData={item} />
        ))}
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 min-h-0">
          {(analysisVideo || analysisVideoThumbnail) && (
            <div
              className="w-full"
              onClick={!analysisVideo && linkedTest ? () => setLinkedTestModalOpen(true) : undefined}
            >
              {analysisVideoCdn === 'yt' ? (
                <YoutubeVideoSolution
                  videoId={analysisVideo}
                  videoThumbnail={analysisVideoThumbnail}
                  isSolution={false}
                  testId={testId}
                  testTitle={testTitle}
                />
              ) : analysisVideoCdn === 'vc' ? (
                <VideoCipherPlayer
                  testId={testId}
                  testTitle={testTitle}
                  videoId={analysisVideo}
                  isSolution={false}
                  videoThumbnail={analysisVideoThumbnail}
                />
              ) : null}
            </div>
          )}

          <Card className="bg-[var(--q3-surface-default)] flex flex-col h-[322px]">
            <CardTitle className="px-6 pb-4 flex flex-row items-center gap-2 border-b flex-shrink-0">
              <PhosphorIcons.LightbulbFilamentIcon size={24} className="text-[var(--q3-neutral-default)]" />
              <div>
                <p className="text-[var(--q3-neutral-default)] font-medium text-sm">Note Down Your Learnings</p>
                <p className="text-[var(--q3-neutral-light)] text-xs font-normal">
                  Add up to 3 things you learned in this test
                </p>
              </div>
            </CardTitle>
            <CardContent className="flex flex-col gap-2 flex-1 overflow-y-auto">
              {[...Array(3)].map((_, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 flex items-center gap-2 justify-start px-3 py-3 border border-dashed border-[var(--q3-stroke-light)] rounded-lg text-[var(--q3-neutral-light)] cursor-pointer"
                  onClick={() => setActiveInput(index)}
                >
                  {activeInput === index ? (
                    <div className="flex items-center w-full">
                      <input
                        type="text"
                        autoFocus
                        placeholder="Add your learning..."
                        value={learnings[index]}
                        onChange={(e) => handleLearningChange(index, e.target.value)}
                        onBlur={() => handleSave(index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        className="flex-1 px-2 py-1 bg-transparent border-none outline-none text-[var(--q3-neutral-default)] placeholder-[var(--q3-neutral-light)]"
                      />
                      <PhosphorIcons.CheckIcon
                        size={20}
                        className="text-[var(--q3-accent-normal)] ml-2 cursor-pointer flex-shrink-0"
                        onClick={() => handleSave(index)}
                      />
                    </div>
                  ) : (
                    <>
                      {!learnings[index] && <PhosphorIcons.PlusIcon size={24} />}
                      <span className="break-words">
                        {learnings[index] ? learnings[index] : 'Click to add your learnings'}
                      </span>
                    </>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
      {linkedTestModalOpen &&
        linkedTest &&
        (isDesktop ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/20"
            onClick={(e) => {
              if (modalRef.current && !modalRef.current.contains(e.target)) setLinkedTestModalOpen(false);
            }}
          >
            <div
              ref={modalRef}
              className="relative bg-[var(--q3-surface-default)] rounded-2xl p-6 max-w-md w-full mx-4"
            >
              <button
                className="absolute z-[1000] -top-12 left-1/2 -translate-x-1/2 p-2 rounded-full bg-[#161616] flex items-center justify-center cursor-pointer hover:scale-[1.01] border shadow-md"
                onClick={() => setLinkedTestModalOpen(false)}
              >
                <PhosphorIcons.XIcon size={20} color="white" />
              </button>
              <div className="flex flex-col items-center text-center gap-4 pt-4">
                <PhosphorIcons.InfoIcon size={40} className="text-[var(--q3-neutral-default)]" />
                <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)]">
                  Please attempt {linkedTest.shortTitle} to view Video Analysis
                </h3>
                <Button
                  className="w-full flex items-center gap-1 justify-center rounded-full px-3 mt-2 bg-[var(--q3-accent-normal)] transition-colors duration-300 hover:bg-[var(--q3-accent-dark)] group"
                  onClick={() => navigate(`/${packId}/${linkedTest._id}/details`)}
                >
                  <span className="text-[var(--q3-neutral-default_inverted)]">Attempt {linkedTest.shortTitle}</span>
                  <PhosphorIcons.ArrowRightIcon size={18} />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center backdrop-blur-sm bg-black/20"
            onClick={(e) => {
              if (modalRef.current && !modalRef.current.contains(e.target)) setLinkedTestModalOpen(false);
            }}
          >
            <div
              ref={modalRef}
              className="relative z-[1000] bg-[var(--q3-surface-default)] rounded-t-2xl p-6 w-full max-h-[80vh]"
            >
              <button
                variant="ghost"
                size="sm"
                className="absolute z-[1000] -top-12 left-1/2 -translate-x-1/2 p-2 rounded-full bg-[#161616] flex items-center justify-center cursor-pointer hover:scale-[1.01] shadow-md"
                onClick={() => setLinkedTestModalOpen(false)}
              >
                <PhosphorIcons.XIcon size={20} color="white" />
              </button>
              <div className="flex flex-col items-center text-center gap-4 pt-4">
                <PhosphorIcons.InfoIcon size={40} className="text-[var(--q3-neutral-default)]" />
                <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)]">
                  Please attempt {linkedTest.title} to view Video Analysis
                </h3>
                <Button
                  className="w-full flex items-center gap-1 justify-center rounded-full px-3 mt-2 bg-[var(--q3-accent-normal)] transition-colors duration-300 hover:bg-[var(--q3-accent-dark)] group"
                  onClick={() => navigate(`/${packId}/${linkedTest._id}/details`)}
                >
                  <span className="text-[var(--q3-neutral-default_inverted)]">Attempt {linkedTest.shortTitle}</span>
                  <PhosphorIcons.ArrowRightIcon size={18} />
                </Button>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}
